'use client';
import {getStrapiData} from '@/app/lib/api';
import {countryOptions} from '@/cst/country';
import {COMMON_ERROR} from '@/cst/errors/common_error';
import {FORM_ERROR} from '@/cst/errors/form_error';
import {cn} from '@/lib/utils';
import clsx from 'clsx';
import parse from 'html-react-parser';
import moment, {Moment} from 'moment';
import {useLocale, useTranslations} from 'next-intl';
import {useSearchParams} from 'next/navigation';
import {useEffect, useRef, useState} from 'react';
import {callFetch} from '../helpers/utils';
import {DatePicker} from '../ui/date-picker';
import Human from './Human';

export default function FormBuilder(prop: {
  page_slug: string;
  form_slug: string;
  meta: any;
}) {
  const t = useTranslations('formBuilder');
  const locale = useLocale();
  const {meta, page_slug, form_slug} = prop;
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [formStatus, setFormStatus] = useState(201);
  const [formData, setFormData] = useState(() => {
    if (typeof window !== 'undefined') {
      const d = sessionStorage.getItem('formData');
      sessionStorage.removeItem('formData');
      return d ? JSON.parse(d) : {};
    }
    return {};
  });

  const hash = useRef<string>(meta.hash);
  const errorMap = Object.entries({...COMMON_ERROR, ...FORM_ERROR}).reduce(
    (a: any, b: any) => {
      const k = ('error.' + b[0]) as any;
      a[b[1].sys_code] = t.has(k) ? t(k) : t('submitFailDescription');
      return a;
    },
    {}
  );
  const tError = (errorCode: string) => {
    return errorMap[errorCode] ?? t('submitFailDescription');
  };

  if (!meta?.sections) {
    meta.sections = [{items: meta.fields}];
  }
  const inputClass =
    'border border-gray-300 rounded-sm w-full text-base p-1 h-[40px]';

  const [errorMsg, setErrorMsg] = useState(t('submitFailDescription'));
  const [fieldErrorMap, setFieldErrorMap] = useState<any>({});
  const [sysCode, setSysCode] = useState('');

  const dateFormat = 'YYYY/MM/DD';
  const dateFormatFull = 'YYYY-MM-DD HH:mm';
  const updateFormData = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >,
    fieldKey: string
  ) => {
    setFormData((prev: any) => ({...prev, [fieldKey]: e.target.value}));
  };

  const addFieldErrorMap = (fieldKey: string, errorMsg: string) => {
    setFieldErrorMap((prev: any) => ({
      ...prev,
      [fieldKey]: errorMsg
    }));
  };

  const extractField = (field: any) => {
    const fieldRow = field[0];
    const fieldBasic = fieldRow.split(':');
    const fieldType = fieldBasic[0];
    const fieldKey = fieldBasic[1].trim();
    const fieldTitle = fieldRow.substr(fieldType.length + fieldKey.length + 2);
    const fieldSettings = field[1];
    return {fieldType, fieldKey, fieldTitle, fieldSettings};
  };

  const formTitle = (
    fieldKey: string,
    fieldTitle: string,
    fieldSettings: any
  ) => {
    const component = (
      <>
        <div>
          <span dangerouslySetInnerHTML={{__html: fieldTitle}}></span>
          {fieldSettings?.required && <span className="text-red-500">*</span>}
        </div>
        {fieldSettings?.description && (
          <div
            dangerouslySetInnerHTML={{__html: fieldSettings?.description}}
          ></div>
        )}
      </>
    );
    const className = cn('text-base', fieldSettings?.t_class);
    return (
      fieldTitle.length > 0 && (
        <div className="mb-2">
          {fieldSettings?.skipFor ? (
            <div {...{className}}>{component}</div>
          ) : (
            <label {...{className}} htmlFor={fieldKey}>
              {component}
            </label>
          )}
        </div>
      )
    );
  };

  const formCheckBox = (
    fieldKey: string,
    fieldTitle: string,
    fieldSettings: any
  ) => (
    <div className="flex items-center justify-start gap-2">
      <div className="flex items-base">
        <input
          name={fieldKey}
          id={fieldKey}
          type="checkbox"
          className={cn(inputClass, 'w-4 h-4', fieldSettings?.class)}
          placeholder={fieldSettings?.placeholder}
          value={formData[fieldKey] ? 'true' : 'false'}
          onChange={(e) => updateFormData(e, fieldKey)}
          required={fieldSettings?.required}
          maxLength={fieldSettings?.maxlength ?? 5000}
          onInput={onInput}
          onInvalid={(e: any) => {
            onInvalid(e, t('missingCheckField'));
          }}
        />
      </div>
      <label
        className={cn('text-base', fieldSettings?.t_class)}
        htmlFor={fieldKey}
      >
        {fieldSettings?.required ? (
          <div className="text-red-500  text-base w-1">*</div>
        ) : (
          <div className="text-base w-1">&nbsp;</div>
        )}
      </label>
      <label
        className={cn(fieldSettings?.t_class ?? 'text-base')}
        htmlFor={fieldKey}
      >
        {parse(fieldTitle)}
      </label>
    </div>
  );

  const formMultiSelectCheckBox = (
    fieldKey: string,
    optionKey: string,
    fieldTitle: string,
    fieldSettings: any
  ) => {
    const key = fieldKey + '_' + optionKey;
    return (
      <div className="flex items-center justify-start gap-2">
        <input
          name={key}
          id={key}
          type="checkbox"
          className={cn(inputClass, 'w-4 h-4', fieldSettings?.class)}
          placeholder={fieldSettings?.placeholder}
          value={formData[key] ? 'true' : 'false'}
          onChange={(e) => {
            setFormData((prev: any) => ({
              ...prev,
              [key]: e.target.checked
            }));
            addFieldErrorMap(fieldKey, '');
          }}
          maxLength={fieldSettings?.maxlength ?? 5000}
          onInput={onInput}
          onInvalid={(e: any) => {
            onInvalid(e, t('missingCheckField'));
          }}
        />
        <label
          className={cn(fieldSettings?.t_class ?? 'text-base')}
          htmlFor={key}
        >
          {parse(fieldTitle)}
        </label>
      </div>
    );
  };

  const formOption = (options: string) => {
    return options.split(';').map((option, index) => {
      const match = option.match(/^([^:]+):(.*)$/);
      const optionValue = match?.[1] ?? option;
      const optionLabel = match?.[2] ?? optionValue;
      return (
        <option key={'option_' + index} value={optionValue}>
          {optionLabel ?? optionValue}
        </option>
      );
    });
  };

  const formRadio = (
    fieldKey: string,
    fieldTitle: string,
    fieldSettings?: any
  ) => {
    const options: string[] = fieldSettings?.options ?? [];
    return options.map((option, index) => {
      const match = option.match(/^([^:]+):(.*)$/);
      const optionValue = match?.[1] ?? option;
      const optionLabel = match?.[2] ?? optionValue;
      const key = fieldKey + '_option_' + index;
      return (
        <label
          key={key}
          htmlFor={key}
          className="flex items-center gap-2 text-base text-gray-900 cursor-pointer py-1"
        >
          <input
            type="radio"
            id={key}
            name={fieldKey}
            value={optionValue}
            checked={formData[fieldKey] === optionValue}
            onChange={(e) => updateFormData(e, fieldKey)}
            onInput={onInput}
            onInvalid={(e: any) => onInvalid(e, fieldTitle, fieldKey)}
            className="h-4 w-4 border-gray-300 text-indigo-600 focus:ring-indigo-500"
          />
          <span>{optionLabel ?? optionValue}</span>
        </label>
      );
    });
  };

  const submitForm = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.target as HTMLFormElement);
    // const data = Object.fromEntries(formData);
    let elementId: string | null = null;
    const isInvalid = meta?.sections
      ?.flatMap((s: any) => s.items || [])
      .map((f: any) => {
        const {fieldType, fieldKey, fieldTitle, fieldSettings} =
          extractField(f);

        switch (fieldType) {
          case 'email':
            if (!validateEmail(fieldKey, fieldSettings)) {
              elementId = elementId ?? fieldKey;
              return 1;
            }
            break;
        }

        if (fieldSettings?.required && !isSkip(fieldSettings?.skip)) {
          switch (fieldType) {
            case 'singleselect':
              if (!formData.get(fieldKey)) {
                addFieldErrorMap(fieldKey, t('plsSelectionAnOption'));
                elementId = elementId ?? fieldKey + '_option_0';
                return 1;
              }
              break;

            case 'multiselect':
              if (
                fieldSettings.options
                  .map((option: string) => {
                    const [optionKey, ..._] = option.split(':');
                    elementId = elementId ?? fieldKey + '_' + optionKey;
                    if (formData.get(fieldKey + '_' + optionKey)) {
                      return 1;
                    }
                    return 0;
                  })
                  .every((a: number) => a === 0)
              ) {
                addFieldErrorMap(fieldKey, t('plsSelectionAnOption'));
                return 1;
              }
              break;

            default:
              if (!formData.get(fieldKey)) {
                elementId = elementId ?? fieldKey;

                addFieldErrorMap(
                  fieldKey,
                  t('plsEnterField', {field: fieldTitle})
                );
                return 1;
              }
              break;
          }
        }
        return 0;
      })
      .some((a: number) => a !== 0);
    if (isInvalid) {
      if (elementId) {
        console.log('invalid: ' + elementId);
        document.getElementById(elementId)?.focus();
      }
      return;
    }

    setIsSubmitting(true);
    setIsSubmitted(false);
    setFormStatus(500);

    formData.append('page_slug', page_slug);
    formData.append('form_slug', form_slug);
    formData.append('hash', hash.current);

    if (meta.data) {
      formData.append('data', JSON.stringify(meta.data));
    }

    try {
      const response = await callFetch(meta.url, {
        method: meta.method,
        body: formData
      });
      setIsSubmitting(false);

      if (response.ok) {
        const res = await response.json();
        const resStatus = res.status;
        setFormStatus(resStatus);
        setErrorMsg(tError(res.sys_code));
        setSysCode(res.sys_code?.toString());
        if (resStatus === 200) {
          setFormData({});
          setFieldErrorMap({});
        }
      }
    } finally {
      setIsSubmitted(true);
      setIsSubmitting(false);
    }
  };

  const onInput = (e: any) => {
    e?.target?.setCustomValidity('');
    addFieldErrorMap(e.target.name, '');
  };
  const onInvalid = (e: any, fieldTitle: string, fieldKey?: string) => {
    const field =
      fieldTitle && fieldTitle.length
        ? fieldTitle.replace(/<[^>]*>/g, '')
        : 'content'; // remove HTML tag
    const msg = t('plsEnterField', {field});
    if (fieldKey && !fieldErrorMap[fieldKey]) {
      if (fieldKey) {
        addFieldErrorMap(fieldKey, msg);
      }
    }
    e?.target?.setCustomValidity(msg);
  };

  const [reservationTimeslots, setReservationTimeslots] = useState<any>({});
  const [selectedReservations, setSelectedReservations] = useState<{
    [key: string]: string;
  }>({});
  const [reservationRules, setReservationRules] = useState<any>({});

  const checkReservation = (date: Date, slug: string) => {
    if (reservationTimeslots[slug]) {
      const dateStr = moment(date).format(dateFormat);
      if (reservationTimeslots[slug][dateStr]) {
        return false;
      }
      return true;
    }
    return true;
  };
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const getReservation = async (slug: string) => {
    (
      await getStrapiData('en', 'reservations', {
        filters: {slug},
        populate: 'schedule'
      })
    )?.data?.map(async (reservation: any) => {
      const excludeDates = (reservation.exclude_dates ?? '').split(',');
      const eventEstMins = reservation.event_estimated_minutes;
      const participantQuota = reservation.participant_quota;
      const bookingLeadTime = moment().subtract(
        reservation.booking_lead_minutes,
        'minutes'
      );

      // sum no_of_participants group by start_time
      const confirmedReservations = (
        (
          await getStrapiData('en', 'reservation-submits', {
            filters: {
              reservation: {documentId: reservation.documentId},
              reservation_status: {$in: ['confirmed', 'pending']},
              start_time: {gte: new Date()}
            },
            pagination: {pageSize: 9999}
          })
        )?.data ?? []
      ).reduce((acc: any, curr: any) => {
        const key = moment(curr.start_time).format(dateFormatFull);
        if (!acc[key]) {
          acc[key] = 0;
        }
        acc[key] += curr.no_of_participants;
        return acc;
      }, {});

      let currentSchedule = reservation.schedule
        ?.filter((s: any) => new Date(s.activate_at) <= today)
        ?.reduce((a: {activate_at: string}, b: {activate_at: string}) => {
          return new Date(a.activate_at) > new Date(b.activate_at) ? a : b;
        }, {}) ?? {activate_at: new Date()};

      const schedules = [
        currentSchedule,
        ...reservation.schedule.filter(
          (s: any) => new Date(s.activate_at) >= today
        )
      ];

      const handleTimeslot = (startTimes: string, currentDate: Moment) => {
        return startTimes
          ?.split(',')
          .filter((value, index, self) => self.indexOf(value) === index)
          .map((startTime) => {
            if (startTime && startTime.length > 0) {
              const startMoment = moment(
                currentDate.format(dateFormat) + ' ' + startTime
              );

              if (participantQuota > 0) {
                const confirmedQuota =
                  confirmedReservations[startMoment.format(dateFormatFull)] ??
                  0;
                if (confirmedQuota > participantQuota) {
                  return null;
                }
              }
              const endTime = moment(startMoment)
                .add(eventEstMins, 'minutes')
                .format('HH:mm');

              if (startMoment.isAfter(bookingLeadTime)) {
                return {
                  value: startTime,
                  label: startTime + ' - ' + endTime
                };
              }
            }
            return null;
          })
          .filter((t) => t);
      };

      let currentDate = new Date();
      currentDate.setHours(0, 0, 0, 0);
      let reservationTime: any = {};
      for (let i = 0; i < reservation.max_booking_advance_days; i++) {
        currentSchedule =
          schedules.find(
            (s: any) =>
              new Date(s.activate_at).toLocaleDateString() ===
              currentDate.toLocaleDateString()
          ) ?? currentSchedule;
        const currentDateStr = moment(currentDate).format(dateFormat);
        if (!excludeDates.includes(currentDateStr)) {
          const weekday = moment(currentDate).format('ddd').toLowerCase();
          if (currentSchedule[weekday]) {
            const timeslots = handleTimeslot(
              currentSchedule[weekday],
              moment(currentDate)
            );
            if (timeslots && timeslots.length > 0) {
              reservationTime[currentDateStr] = timeslots;
            }
          }
        }
        currentDate.setDate(currentDate.getDate() + 1);
      }
      setReservationTimeslots((prev: any) => ({
        ...prev,
        [reservation.slug]: reservationTime
      }));
      setReservationRules((prev: any) => ({
        ...prev,
        [reservation.slug]: reservation.invite_rules
      }));
    });
  };

  const getReservationTimeslot = (slug: string) => {
    return reservationTimeslots[slug]?.[selectedReservations[slug]] ?? [];
  };

  const wordCount = (text: string) => {
    if (!text || text.trim() === '') return 0;
    const matches = text.match(/[\u4e00-\u9fa5]|[a-zA-Z0-9]+/g);
    return matches ? matches.length : 0;
  };

  const validateEmail = (fieldKey: string, fieldSettings: any) => {
    const fieldValue = formData[fieldKey];
    if (fieldValue) {
      if (fieldSettings?.reservation_data) {
        const reservation_slug = fieldSettings?.reservation_data.split(':')[0];
        if (
          reservationRules[reservation_slug] &&
          reservationRules[reservation_slug].length > 0
        ) {
          let isInvitedEmail = false;
          for (const invite_rule of reservationRules[reservation_slug]) {
            const ruleValue = invite_rule.value.toLowerCase();
            ruleValue.split('\n').map((ruleLine: string) => {
              if (ruleLine.trim().length > 0) {
                if (invite_rule.type === 'email') {
                  if (ruleLine.trim() === fieldValue) {
                    isInvitedEmail = true;
                  }
                }
                if (invite_rule.type === 'domain') {
                  if (fieldValue.endsWith('@' + ruleLine.trim())) {
                    isInvitedEmail = true;
                  }
                }
              }
            });
          }
          if (!isInvitedEmail) {
            addFieldErrorMap(fieldKey, t('error.NOT_VALID_RESERVATION_EMAIL'));
            return false;
          }
        }
      }

      // check if email is valid format
      const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
      if (!emailRegex.test(fieldValue)) {
        addFieldErrorMap(fieldKey, t('error.NOT_VALID_EMAIL'));
        return false;
      }
    }
    return true;
  };

  const reservation_slugs: string[] = [];
  const searchParams = useSearchParams();

  useEffect(() => {
    if (searchParams.get('email')) {
      setFormData((prev: any) => ({
        ...prev,
        ['email']: searchParams.get('email')
      }));
    }
    meta?.sections?.forEach((section: any) => {
      section.items?.forEach((field: any) => {
        if (field?.[1]?.reservation_date) {
          const reservation_slug = field[1].reservation_date;
          if (!reservation_slugs.includes(reservation_slug)) {
            getReservation(reservation_slug);
            reservation_slugs.push(reservation_slug);
          }
        }
      });
    });
  }, []);

  const dClass = 'w-full md:col-span-6';

  const isSkip = (skipConditions: any) => {
    if (!skipConditions || !Array.isArray(skipConditions)) return false;
    return skipConditions.every((skip: any) => {
      const key = skip.key;
      if (skip.nin) {
        return !skip.nin.includes(formData[key]);
      }
      return false;
    });
  };

  return (
    <div className="questrial text-[18px]">
      <div>
        <div>
          {isSubmitting && (
            <div className="h-[40vh] flex flex-col justify-center items-center text-center text-gray-500">
              <div className="text-[#e31836]">
                <span className="material-symbols thin text-[100px] animate-spin">
                  progress_activity
                </span>
              </div>
            </div>
          )}
          {isSubmitted && (
            <div className="min-h-[40vh] flex flex-col justify-center items-center text-center text-gray-500">
              {formStatus != 200 ? (
                <div className="text-[#e31836]">
                  <span className="material-symbols thin text-[100px]">
                    error
                  </span>
                  <div className="text-2xl">{t('submitFail')}</div>
                  <div className="px-4 pt-4 max-w-[450px] m-auto text-md leading-6">
                    {errorMsg}
                  </div>
                  {['9901002', '9901003'].includes(sysCode) ? (
                    <button
                      className="bg-[#e31836] text-white text-lg py-2 px-4 rounded-md my-10 hover:shadow-lg w-[250px]"
                      onClick={() => {
                        sessionStorage.setItem(
                          'formData',
                          JSON.stringify(formData)
                        );
                        window.location.reload();
                      }}
                    >
                      {t('reloadPage')}
                    </button>
                  ) : (
                    <button
                      className="bg-[#e31836] text-white text-lg py-2 px-4 rounded-md my-10 hover:shadow-lg w-[250px]"
                      onClick={() => {
                        setIsSubmitted(false);
                      }}
                    >
                      {t('retry')}
                    </button>
                  )}
                </div>
              ) : (
                <div className="text-[#e31836]">
                  <span className="material-symbols thin text-[100px]">
                    check_circle
                  </span>

                  {meta?.success ? (
                    <div
                      dangerouslySetInnerHTML={{__html: meta?.success}}
                    ></div>
                  ) : (
                    <div className="text-2xl">{t('submitSuccess')}</div>
                  )}
                  <button
                    className="bg-[#e31836] text-white text-lg py-2 px-4 rounded-md my-10 hover:shadow-lg w-[250px]"
                    onClick={() => {
                      setIsSubmitted(false);
                    }}
                  >
                    {t('submitAgain')}
                  </button>
                </div>
              )}
            </div>
          )}
          {!isSubmitted && !isSubmitting && (
            <form
              name="form"
              onSubmit={submitForm}
              className="flex flex-col w-full gap-4 pb-10"
            >
              {meta?.sections?.map((section: any, sindex: number) => {
                return (
                  <div
                    className={clsx(
                      section.class,
                      'grid md:grid-cols-6 w-full gap-4 pb-10'
                    )}
                    key={sindex}
                  >
                    {section.items.map((field: any, index: number) => {
                      const fieldRow = field[0];
                      const fieldBasic = fieldRow.split(':');
                      const fieldType = fieldBasic[0];
                      const fieldKey = fieldBasic[1].trim();
                      const fieldTitle = fieldRow.substr(
                        fieldType.length + fieldKey.length + 2
                      );
                      const fieldSettings = field[1];
                      const optionGroups: string[] = [];
                      const reservation_slug = fieldSettings?.reservation_time;

                      const isQuestionSkip = isSkip(fieldSettings?.skip);
                      const isRequired =
                        fieldSettings?.required && !isQuestionSkip;

                      if (isQuestionSkip) {
                        return null;
                      }

                      const component = () => {
                        switch (fieldType) {
                          case 'title':
                            return (
                              <div>
                                {formTitle('', fieldKey, {
                                  t_class: 'font-bold',
                                  ...fieldSettings
                                })}
                              </div>
                            );
                          case 'dropdown':
                          case 'select':
                            if (typeof fieldSettings?.options == 'string') {
                              optionGroups.push(fieldSettings?.options);
                            } else {
                              fieldSettings?.options.map((option: string) => {
                                optionGroups.push(option);
                              });
                            }

                            return (
                              <div>
                                {formTitle(fieldKey, fieldTitle, fieldSettings)}
                                <div className="grid grid-cols-1 ">
                                  <select
                                    name={fieldKey}
                                    id={fieldKey}
                                    className={cn(
                                      'col-start-1 row-start-1 w-full appearance-none rounded-sm bg-white py-1.5',
                                      'pr-8 pl-3 text-gray-900 border border-gray-300 text-base'
                                    )}
                                    value={formData[fieldKey]}
                                    onChange={(e) =>
                                      updateFormData(e, fieldKey)
                                    }
                                    required={isRequired}
                                    onInput={onInput}
                                    onInvalid={(e: any) => {
                                      onInvalid(e, fieldTitle, fieldKey);
                                    }}
                                  >
                                    {reservation_slug
                                      ? getReservationTimeslot(
                                          reservation_slug
                                        ).map((a: any) => {
                                          return (
                                            <option
                                              key={a.value}
                                              value={a.value}
                                            >
                                              {a.label}
                                            </option>
                                          );
                                        })
                                      : optionGroups.map(
                                          (optionGroup, index) => {
                                            const optionGroupLabel =
                                              optionGroup.indexOf('=') > 1
                                                ? optionGroup.split('=')[0]
                                                : null;
                                            if (optionGroupLabel) {
                                              return (
                                                <optgroup
                                                  key={'optgroup_' + index}
                                                  label={optionGroupLabel}
                                                >
                                                  {formOption(
                                                    optionGroup.substring(
                                                      optionGroup.indexOf('=') +
                                                        1
                                                    )
                                                  )}
                                                </optgroup>
                                              );
                                            } else {
                                              return formOption(optionGroup);
                                            }
                                          }
                                        )}
                                  </select>
                                  <div className="pointer-events-none col-start-1 row-start-1 mr-2 pt-2 justify-self-end text-gray-500">
                                    <span className="material-symbols outlined text-base">
                                      unfold_more
                                    </span>
                                  </div>
                                </div>
                              </div>
                            );
                          case 'singleselect':
                            return (
                              <div>
                                {formTitle(fieldKey, fieldTitle, {
                                  ...fieldSettings,
                                  skipFor: true
                                })}
                                <div className="mt-2 grid grid-cols-1 ">
                                  {reservation_slug
                                    ? getReservationTimeslot(
                                        reservation_slug
                                      ).map((a: any) => {
                                        return (
                                          <option key={a.value} value={a.value}>
                                            {a.label}
                                          </option>
                                        );
                                      })
                                    : formRadio(
                                        fieldKey,
                                        fieldTitle,
                                        fieldSettings
                                      )}
                                </div>
                              </div>
                            );
                          case 'multiselect':
                            const multipleOptions: any = [];
                            fieldSettings?.options.map((option: string) => {
                              multipleOptions.push(option);
                            });
                            return (
                              <div>
                                {formTitle(fieldKey, fieldTitle, {
                                  ...fieldSettings,
                                  skipFor: true
                                })}
                                <div className="mt-2 grid grid-cols-1 ">
                                  {multipleOptions.map(
                                    (option: any, mindex: number) => {
                                      const [optionKey, ...optionText] =
                                        option.split(':');

                                      return (
                                        <div key={'fieldKey' + mindex}>
                                          {formMultiSelectCheckBox(
                                            fieldKey,
                                            optionKey,
                                            optionText.join(':'),
                                            {
                                              ...fieldSettings,
                                              t_class: 'text-base'
                                            }
                                          )}
                                        </div>
                                      );
                                    }
                                  )}
                                </div>
                              </div>
                            );
                          case 'country':
                            return (
                              <div>
                                {formTitle(fieldKey, fieldTitle, fieldSettings)}
                                <div className="mt-2 grid grid-cols-1 ">
                                  <select
                                    name={fieldKey}
                                    id={fieldKey}
                                    className={cn(
                                      'col-start-1 row-start-1 w-full appearance-none rounded-sm bg-white py-1.5',
                                      'pr-8 pl-3 text-gray-900 border border-gray-300 text-base'
                                    )}
                                    value={formData[fieldKey]}
                                    onChange={(e) =>
                                      updateFormData(e, fieldKey)
                                    }
                                    required={isRequired}
                                    onInput={onInput}
                                    onInvalid={(e: any) => {
                                      onInvalid(e, fieldTitle, fieldKey);
                                    }}
                                  >
                                    {countryOptions.map((option, index) => (
                                      <option
                                        key={'option_' + index}
                                        value={option.key}
                                      >
                                        {locale === 'en'
                                          ? option.en
                                          : option.zh}
                                      </option>
                                    ))}
                                  </select>
                                  <div className="pointer-events-none col-start-1 row-start-1 mr-2  justify-self-end text-gray-500">
                                    <span className="material-symbols outlined text-base">
                                      unfold_more
                                    </span>
                                  </div>
                                </div>
                              </div>
                            );
                          case 'email':
                            return (
                              <div>
                                {formTitle(fieldKey, fieldTitle, fieldSettings)}
                                <input
                                  name={fieldKey}
                                  id={fieldKey}
                                  type="text"
                                  placeholder={fieldSettings?.placeholder}
                                  className={cn(
                                    inputClass,
                                    fieldSettings?.class
                                  )}
                                  value={formData[fieldKey] || ''}
                                  onChange={(e) => updateFormData(e, fieldKey)}
                                  onInput={onInput}
                                />
                              </div>
                            );
                          case 'file':
                            return (
                              <div className={'mb-4'}>
                                {formTitle(fieldKey, fieldTitle, fieldSettings)}
                                <input
                                  name={fieldKey}
                                  id={fieldKey}
                                  type="file"
                                  className={cn(
                                    inputClass,
                                    fieldSettings?.class
                                  )}
                                  onChange={(e) => {
                                    const files = e.target.files;
                                    setFormData((prev: any) => ({
                                      ...prev,
                                      [fieldKey]: files ? files[0] : null
                                    }));
                                  }}
                                  required={isRequired}
                                  onInput={onInput}
                                  onInvalid={(e: any) => {
                                    onInvalid(e, fieldTitle, fieldKey);
                                  }}
                                />
                              </div>
                            );
                          case 'textarea':
                            return (
                              <div>
                                {formTitle(fieldKey, fieldTitle, fieldSettings)}
                                <textarea
                                  name={fieldKey}
                                  id={fieldKey}
                                  className={cn(
                                    inputClass,
                                    'resize-y',
                                    fieldSettings?.class
                                  )}
                                  placeholder={fieldSettings?.placeholder}
                                  value={formData[fieldKey] || ''}
                                  onChange={(e) => {
                                    if (
                                      fieldSettings?.maxWord &&
                                      wordCount(e.target.value) >
                                        fieldSettings.maxWord
                                    ) {
                                      return;
                                    }
                                    updateFormData(e, fieldKey);
                                  }}
                                  maxLength={fieldSettings?.maxlength ?? 5000}
                                  onInput={onInput}
                                />
                                {fieldSettings?.maxWord && (
                                  <div className="flex gap-1 items-center justify-end text-xs ">
                                    <div>{wordCount(formData[fieldKey])}</div>
                                    <div>/</div>
                                    <div>{fieldSettings?.maxWord}</div>
                                  </div>
                                )}
                              </div>
                            );
                          case 'date':
                            return (
                              <div>
                                <input
                                  type="hidden"
                                  name={fieldKey}
                                  value={(
                                    formData[fieldKey] ?? new Date()
                                  ).toLocaleDateString()}
                                />
                                {formTitle(fieldKey, fieldTitle, fieldSettings)}
                                <DatePicker
                                  className={cn(
                                    inputClass,
                                    fieldSettings?.class
                                  )}
                                  placeholder={fieldSettings?.placeholder}
                                  value={formData[fieldKey] || ''}
                                  onChange={(date: Date) => {
                                    setFormData((prev: any) => ({
                                      ...prev,
                                      [fieldKey]: date
                                    }));
                                    setSelectedReservations((prev: any) => {
                                      const selectDate =
                                        moment(date).format(dateFormat);
                                      meta?.sections?.forEach(
                                        (section: any) => {
                                          section.items?.forEach((f: any) => {
                                            const fRow = f[0];
                                            const fBasic = fRow.split(':');
                                            const fKey = fBasic[1];
                                            const fSettings = f[1];
                                            const slug =
                                              fSettings?.reservation_time;
                                            if (
                                              slug ===
                                              fieldSettings?.reservation_date
                                            ) {
                                              const timeslot =
                                                reservationTimeslots[slug];
                                              if (
                                                !timeslot?.[selectDate]?.find(
                                                  (t: any) =>
                                                    t.value === formData[fKey]
                                                )
                                              ) {
                                                setFormData((prev: any) => ({
                                                  ...prev,
                                                  [fKey]:
                                                    timeslot?.[selectDate]?.[0]
                                                      .value ?? ''
                                                }));
                                              }
                                            }
                                          });
                                        }
                                      );

                                      return {
                                        ...prev,
                                        [fieldSettings?.reservation_date]:
                                          selectDate
                                      };
                                    });
                                  }}
                                  required={isRequired}
                                  maxLength={fieldSettings?.maxlength ?? 5000}
                                  onInput={onInput}
                                  onInvalid={(e: any) => {
                                    onInvalid(e, fieldTitle, fieldKey);
                                  }}
                                  {...(fieldSettings?.reservation_date
                                    ? {
                                        disabled: (d: Date) =>
                                          checkReservation(
                                            d,
                                            fieldSettings?.reservation_date
                                          )
                                      }
                                    : {})}
                                />
                              </div>
                            );
                          case 'checkbox':
                            return (
                              <div>
                                {formCheckBox(
                                  fieldKey,
                                  fieldTitle,
                                  fieldSettings
                                )}
                              </div>
                            );
                          case 'integer':
                            return (
                              <div>
                                {formTitle(fieldKey, fieldTitle, fieldSettings)}
                                <input
                                  type="number"
                                  name={fieldKey}
                                  id={fieldKey}
                                  className={cn(
                                    inputClass,
                                    fieldSettings?.class
                                  )}
                                  placeholder={fieldSettings?.placeholder}
                                  value={formData[fieldKey] || ''}
                                  onChange={(e) => {
                                    if (e.target.value == '') {
                                      updateFormData(e, fieldKey);
                                    }
                                    // check if the input is a number
                                    if (isNaN(parseInt(e.target.value))) {
                                      return;
                                    }
                                    updateFormData(e, fieldKey);
                                  }}
                                  onBlur={(e) => {
                                    const value = parseInt(e.target.value);
                                    const min = fieldSettings?.min;
                                    const max = fieldSettings?.max;
                                    if (min && value < min) {
                                      e.target.value = fieldSettings?.min;
                                      updateFormData(e, fieldKey);
                                    }
                                    if (max && value > max) {
                                      e.target.value = fieldSettings?.max;
                                      updateFormData(e, fieldKey);
                                    }
                                  }}
                                  maxLength={fieldSettings?.maxlength ?? 5000}
                                  onInput={onInput}
                                />
                              </div>
                            );
                          case 'html':
                            return (
                              <div
                                dangerouslySetInnerHTML={{
                                  __html: fieldSettings?.html
                                }}
                              ></div>
                            );
                          default:
                            return (
                              <div>
                                {formTitle(fieldKey, fieldTitle, fieldSettings)}
                                <input
                                  name={fieldKey}
                                  id={fieldKey}
                                  className={cn(
                                    inputClass,
                                    fieldSettings?.class
                                  )}
                                  placeholder={fieldSettings?.placeholder}
                                  value={formData[fieldKey] || ''}
                                  onChange={(e) => updateFormData(e, fieldKey)}
                                  maxLength={fieldSettings?.maxlength ?? 5000}
                                  onInput={onInput}
                                />
                              </div>
                            );
                        }
                      };

                      return (
                        <div
                          key={index}
                          className={fieldSettings?.class ?? dClass}
                        >
                          {component()}
                          {fieldErrorMap[fieldKey] && (
                            <div className="text-[#e31836] text-xs mt-2">
                              {fieldErrorMap[fieldKey]}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                );
              })}

              <Human hash={hash}>
                <button
                  type="submit"
                  className={cn(
                    'bg-[#e31836] text-white text-lg font-bold py-2 px-4 rounded-md my-10',
                    'hover:shadow-lg  md:w-fit'
                  )}
                >
                  {t('submit')}
                </button>
              </Human>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
